import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { Plus, Edit, Trash2, Search, Car } from 'lucide-react'
import toast from 'react-hot-toast'
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card'
import { Button } from '../components/ui/Button'
import { Input } from '../components/ui/Input'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/Table'
import { Badge } from '../components/ui/Badge'
import { enumsApi } from '../lib/api'
import { useLanguage } from '../contexts/LanguageContext'

const languageLabels = {
  az: 'Azərbaycan',
  ru: 'Русский',
  en: 'English',
  ko: '한국어',
  kk: 'Қазақша',
  ky: 'Кыргызча'
}

export default function VehicleTypes() {
  const [filters, setFilters] = useState({ search: '', page: 1, limit: 100 })
  const [showForm, setShowForm] = useState(false)
  const [editingId, setEditingId] = useState(null)
  const [formData, setFormData] = useState({
    name_az: '', name_ru: '', name_en: '', name_ko: '', name_kk: '', name_ky: ''
  })
  const { currentLanguage } = useLanguage()
  const queryClient = useQueryClient()

  const { data, isLoading, error } = useQuery(
    ['vehicle-types', filters],
    () => enumsApi.vehicleTypes.getAll(filters),
    { select: (d) => d?.data?.data, keepPreviousData: true, retry: 1, refetchOnWindowFocus: false }
  )

  const deleteMutation = useMutation(
    (id) => enumsApi.vehicleTypes.delete(id),
    {
      onSuccess: () => {
        toast.success('Vehicle type deleted successfully')
        queryClient.invalidateQueries(['vehicle-types'])
      }
    }
  )

  const createMutation = useMutation(
    (data) => enumsApi.vehicleTypes.create(data),
    {
      onSuccess: () => {
        toast.success('Vehicle type created successfully')
        queryClient.invalidateQueries(['vehicle-types'])
        setShowForm(false)
        resetForm()
      }
    }
  )

  const updateMutation = useMutation(
    (data) => enumsApi.vehicleTypes.update(editingId, data),
    {
      onSuccess: () => {
        toast.success('Vehicle type updated successfully')
        queryClient.invalidateQueries(['vehicle-types'])
        setShowForm(false)
        setEditingId(null)
        resetForm()
      }
    }
  )

  const resetForm = () => {
    setFormData({ name_az: '', name_ru: '', name_en: '', name_ko: '', name_kk: '', name_ky: '' })
  }

  const handleEdit = (vt) => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
    setEditingId(vt.id)
    setFormData({
      name_az: vt.name_az || vt.name || '',
      name_ru: vt.name_ru || vt.name || '',
      name_en: vt.name_en || vt.name || '',
      name_ko: vt.name_ko || vt.name || '',
      name_kk: vt.name_kk || vt.name_ru || vt.name || '',
      name_ky: vt.name_ky || vt.name_ru || vt.name || ''
    })
    setShowForm(true)
  }

  const handleDelete = (id) => {
    if (window.confirm('Are you sure you want to delete this vehicle type?')) {
      deleteMutation.mutate(id)
    }
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    if (editingId) {
      updateMutation.mutate(formData)
    } else {
      createMutation.mutate(formData)
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Vehicle Types</h1>
          <p className="mt-1 text-sm text-gray-600">
            Manage vehicle type options (e.g. N-Line, Quadro, GT, Sport)
          </p>
        </div>
        <Button onClick={() => setShowForm(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Add Vehicle Type
        </Button>
      </div>

      {showForm && (
        <Card>
          <CardHeader>
            <CardTitle>{editingId ? 'Edit Vehicle Type' : 'Add New Vehicle Type'}</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Names in Different Languages</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {Object.entries(languageLabels).map(([lang, label]) => (
                    <div key={lang}>
                      <label className="label">
                        Name ({label}) *
                        {lang === currentLanguage && (
                          <span className="ml-2 text-xs text-primary-600 font-medium">(Current Language)</span>
                        )}
                      </label>
                      <Input
                        placeholder={`Enter name in ${label}`}
                        value={formData[`name_${lang}`]}
                        onChange={(e) => setFormData(prev => ({ ...prev, [`name_${lang}`]: e.target.value }))}
                        required
                      />
                    </div>
                  ))}
                </div>
              </div>
              <div className="flex justify-end space-x-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => { setShowForm(false); setEditingId(null); resetForm() }}
                >
                  Cancel
                </Button>
                <Button type="submit" loading={createMutation.isLoading || updateMutation.isLoading}>
                  {editingId ? 'Update' : 'Create'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Search className="h-5 w-5 mr-2" />
            Search Vehicle Types
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="max-w-md">
            <Input
              placeholder="Search vehicle types..."
              value={filters.search}
              onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value, page: 1 }))}
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Vehicle Types ({data?.count || 0})</CardTitle>
        </CardHeader>
        <CardContent>
          {error ? (
            <div className="text-center py-12">
              <p className="text-red-600">Error loading vehicle types: {error.message}</p>
            </div>
          ) : isLoading ? (
            <div className="space-y-4">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="animate-pulse">
                  <div className="h-4 bg-gray-200 rounded w-full mb-2" />
                  <div className="h-4 bg-gray-200 rounded w-3/4" />
                </div>
              ))}
            </div>
          ) : data?.results?.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Code</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.results.map((vt) => (
                  <TableRow key={vt.id}>
                    <TableCell>
                      <div className="flex items-center">
                        <Car className="h-4 w-4 mr-2 text-gray-400" />
                        {vt.name}
                      </div>
                    </TableCell>
                    <TableCell>
                      <code className="text-xs bg-gray-100 px-2 py-1 rounded">{vt.code}</code>
                    </TableCell>
                    <TableCell>
                      <Badge variant={vt.is_active ? 'success' : 'gray'}>
                        {vt.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center space-x-2">
                        <Button variant="ghost" size="sm" onClick={() => handleEdit(vt)}>
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-600 hover:text-red-700"
                          onClick={() => handleDelete(vt.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-12">
              <Car className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No vehicle types</h3>
              <p className="mt-1 text-sm text-gray-500">Get started by adding your first vehicle type.</p>
              <div className="mt-6">
                <Button onClick={() => setShowForm(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Vehicle Type
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
